import { useEffect, useRef, useState } from 'react';

import { useCurrentTime } from '@/hooks/useCurrentTime';

const estimatedDurationMs = 8 * 60 * 1000;
const maximumEstimatedProgress = 98;
const updateIntervalMs = 1000;
const completionHoldMs = 700;

/** Estimated pacing only. Workflow completion is the sole source of 100%. */
export function useEstimatedSetupProgress({
  running,
  failed,
  startedAt,
  scopeKey,
  clockMode,
}: {
  running: boolean;
  failed: boolean;
  startedAt?: Date | string | null;
  scopeKey?: string;
  clockMode: 'milestone' | 'continuous';
}) {
  const now = useCurrentTime(updateIntervalMs).getTime();
  const [showFinishing, setShowFinishing] = useState(running);
  const clock = useRef({ scopeKey, clockMode, startedAt: Number.NaN });
  const scopeChanged = clock.current.scopeKey !== scopeKey;
  // Navigation can reuse the component while another household is still running.
  if (scopeChanged) {
    setShowFinishing(running);
  }
  if (scopeChanged || clock.current.clockMode !== clockMode || !running) {
    clock.current = { scopeKey, clockMode, startedAt: Number.NaN };
  }
  const timestamp =
    startedAt instanceof Date
      ? startedAt.getTime()
      : Date.parse(startedAt ?? '');
  // Standalone jobs share one clock while processing stays active. A newer job
  // must not reset it; a late timestamp can move the start earlier.
  if (clockMode === 'continuous' && running && Number.isFinite(timestamp)) {
    clock.current.startedAt = Number.isFinite(clock.current.startedAt)
      ? Math.min(clock.current.startedAt, timestamp)
      : timestamp;
  }
  // Onboarding always uses the saved milestone, even when it arrives late.
  // A missing timestamp must not start a browser-local clock in either mode.
  const effectiveStart =
    clockMode === 'continuous' ? clock.current.startedAt : timestamp;

  useEffect(() => {
    if (running) {
      setShowFinishing(true);
      return;
    }
    if (!showFinishing) return;
    // Keep the panel mounted long enough to show the actual completed value.
    const timer = setTimeout(() => setShowFinishing(false), completionHoldMs);
    return () => clearTimeout(timer);
  }, [running, showFinishing]);

  const elapsed = Number.isFinite(effectiveStart)
    ? Math.max(0, now - effectiveStart)
    : 0;
  // About 93% at eight minutes, then progressively slower, never above 98%.
  const estimate =
    maximumEstimatedProgress *
    (1 - Math.exp((-3 * elapsed) / estimatedDurationMs));
  return {
    progress: !running && !failed ? 100 : estimate,
    showProcessing: running || (showFinishing && !failed),
  };
}
